import * as RxDialog from '@radix-ui/react-dialog';
import { formatCode, normalizeCode, reasonMessage, testCodeKind } from '@pl/domain';
import type { ReasonCode, ScanRequest, ScanResponse } from '@pl/domain';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ApiError } from '../../lib/api';
import { qk } from '../../lib/queries';
import { uuid } from '../../lib/uuid';
import { useQueue, useQueueState } from '../queue/QueueProvider';
import { RedScreen } from '../RedScreen';
import { announceRefusal, registerRefusalCloser } from '../refusalGuard';
import { type LocalResult, ResultPanel } from '../ResultPanel';
import { feedback } from '../scanner/feedback';
import { useScanSite } from '../useScanSite';
import s from './scanKit.module.css';

/*
 * What every scan screen that works on one job — a transfer out, a transfer in, a count — shares.
 * These are the rules the earlier screens learned by drifting apart: the result follows the queue,
 * refusals go through `showRed`, the id is minted at capture, the job is re-asked for whenever one
 * of its scans lands. They live once, here; each screen passes only what is its own.
 */

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** The one phrase for "a scan for this job is still on the phone", wherever it locks something. */
export const waitingFor = (n: number) => `Waiting for ${plural(n, 'scan', 'scans')} to send`;

/** What an HTTP refusal of the whole job is called on the phone, by error code. */
export type FatalTable = Record<string, { title: string; note: string }>;

/**
 * Every time the server answers a scan for this job, the job is asked for again — whether the
 * worker was watching that scan or not. A scan that queued behind a dead signal lands in a flush
 * minutes later with no reply for this screen to read, and a replayed answer's tally can be stale.
 *
 * Arriving on the screen is itself a reason to ask, so the baseline starts empty. A scan can land
 * while the worker is on another screen; its settle is then already counted when this screen
 * mounts, and the cached job (fresh for 15 s) would show the old tally with its lock open beside
 * it. This is chosen over `staleTime: 0` on the query because the rule belongs to the scan screens,
 * not to every reader of the job, and it keeps one place that says when the tally is re-asked.
 *
 * `keys` is exactly what is re-asked, and nothing more: a count re-asks only for itself, since
 * anything else it touched could carry the figure it is blind to.
 */
export function useRefetchOnSettle(settled: number, keys: QueryKey[]): void {
  const qc = useQueryClient();
  const seen = useRef<number | null>(null);
  // Fresh arrays each render; the effect runs on `settled` alone and reads the latest through a ref.
  const keysRef = useRef(keys);
  keysRef.current = keys;
  useEffect(() => {
    if (settled === seen.current) return;
    seen.current = settled;
    for (const queryKey of keysRef.current) void qc.invalidateQueries({ queryKey });
  }, [settled, qc]);
}

/**
 * The last scan's card, brought into view when a new scan lands. On a phone the tally above the
 * scanner can push it under the footer, and a green that nobody sees is a box the worker scans
 * again. It moves only for a new scan (`revealOn`), never when the queue ticks, so a worker who has
 * scrolled up to read the tally is not dragged back down.
 */
export function RevealedResult({ result, revealOn }: { result: LocalResult | null; revealOn: unknown }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (revealOn) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [revealOn]);
  if (!result) return null;
  return (
    <div ref={ref} className={s.tkReveal}>
      <ResultPanel result={result} />
    </div>
  );
}

/**
 * The bottom sheet a scan screen's decisions are made in: send anyway, cancel, close, finish with
 * nothing scanned, write off.
 *
 * A refusal can land while it is open — the camera and a Bluetooth scanner keep reading behind a
 * sheet — and a Radix sheet left open keeps the red screen `aria-hidden` and steals its focus. So
 * while open it registers with the refusal guard: the red screen's `announceRefusal` closes it in
 * the same commit that mounts the red, and focus is not handed back to the button that opened it.
 *
 * `safeFocus` is where focus lands when the sheet opens. Radix would otherwise take the first button,
 * which on a destructive sheet is the destructive answer: a hardware keyboard's Enter would confirm
 * it. Pointing it at the way back makes Enter the harmless answer.
 */
export function ScanSheet({
  open,
  onClose,
  busy = false,
  title,
  lead,
  children,
  actions,
  safeFocus,
}: {
  open: boolean;
  onClose: () => void;
  busy?: boolean;
  title: string;
  lead: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
  safeFocus?: RefObject<HTMLElement | null>;
}) {
  const suppressFocus = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    return registerRefusalCloser(() => {
      suppressFocus.current = true;
      closeRef.current();
    });
  }, [open]);

  return (
    <RxDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next || busy) return;
        onClose();
      }}
    >
      <RxDialog.Portal>
        <RxDialog.Overlay className={s.tkScrim} />
        <RxDialog.Content
          className={s.tkSheet}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            if (!safeFocus?.current) return;
            e.preventDefault();
            safeFocus.current.focus();
          }}
          onCloseAutoFocus={(e) => {
            if (!suppressFocus.current) return;
            e.preventDefault();
            suppressFocus.current = false;
          }}
        >
          <RxDialog.Title className={s.tkTitle}>{title}</RxDialog.Title>
          <p className={s.tkLead}>{lead}</p>
          {children}
          <div className={s.tkActions}>{actions}</div>
        </RxDialog.Content>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}

/** Where the one button on a red screen goes: the next box, back to the job's list, or sign-in. */
export type RedExit = 'resume' | 'back' | 'login';

export interface ScanRed {
  title: string;
  message: string;
  code?: string;
  kicker: string;
  note: string;
  exit: RedExit;
  /** Set only on a refusal a lead may overrule: the code to send again. */
  overridable?: string;
}

/** Everything that differs between one scan screen's scans and another's, apart from the job. */
export interface ScanFlowWords {
  mode: ScanRequest['mode'];
  /** The red screen's kicker: "Not added", "Not received", "Not counted". The job's label follows it when there is one. */
  refused: string;
  /** What a queued scan keeps locked: "Send", "Close", "Finish". */
  locks: string;
  /** What to do with a refused sticker: put it back, set it aside. */
  refusalNote: string;
  /** "Nothing was changed" or "Nothing was counted": said on a refusal of the whole job, and on a sign-out. */
  unchanged: string;
  titles: Partial<Record<ReasonCode, string>>;
  fallbackTitle: string;
  fatal: FatalTable;
  /** The amber line under a duplicate or an ignored sticker. */
  notTaken: string;
  /** The green for a scan the server took. */
  accepted: (response: ScanResponse, code: string | undefined) => LocalResult;
  /** A refusal a lead may overrule on this screen. Only transfer Out has one. */
  overridable?: (response: ScanResponse) => boolean;
}

export interface ScanFlowJob {
  /** The queue context every scan for this job shares, so one lock covers them all. */
  context: string;
  /** Sent with each scan: the transfer or the count. */
  contextId: string;
  /** Shown after the kicker: the transfer's code. A count has none to show. */
  label?: string;
  /** Re-asked for when one of this job's scans settles, and on arrival. Nothing else is. */
  refetchKeys: QueryKey[];
  /** Folds a scan's own answer into the cached job, so the tally moves the moment it is answered. */
  onResponse: (response: ScanResponse) => void;
  /**
   * A refusal of the whole job the screen takes over itself — the job ended under the phone — so no
   * red screen is shown for it, not for one frame. True when it took it.
   */
  onJobEnded?: (error: ApiError) => boolean;
}

export function useScanFlow(job: ScanFlowJob, words: ScanFlowWords) {
  const { site } = useScanSite();
  const queue = useQueue();
  const state = useQueueState(job.context);
  const [result, setResult] = useState<LocalResult | null>(null);
  /** Which scan the panel is describing, so it can follow that scan when it settles. */
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [red, setRed] = useState<ScanRed | null>(null);
  // Fresh objects each render; the callbacks read them through refs so they stay put.
  const w = useRef(words);
  w.current = words;
  const j = useRef(job);
  j.current = job;

  useRefetchOnSettle(state.settled, job.refetchKeys);

  // Never `setRed` directly: the red screen and the closing of any open sheet must land in the same
  // commit, or the sheet's focus trap pulls focus back into a sheet about to disappear.
  const showRed = useCallback((next: ScanRed) => {
    announceRefusal();
    setRed(next);
  }, []);

  const titleFor = useCallback((reason: ReasonCode | null) => (reason && w.current.titles[reason]) || w.current.fallbackTitle, []);
  const kickerNow = useCallback(() => (j.current.label ? `${w.current.refused} · ${j.current.label}` : w.current.refused), []);

  /** The panel is the last scan's standing, read off the queue, not a souvenir of the moment it was taken. */
  const shownResult = useMemo<LocalResult | null>(() => {
    if (!result || !resultFor) return result;
    const item = queue.get(resultFor);
    if (!item || item.status === 'pending') return result;
    // A refusal shown live keeps the name it was given; one that comes back refused only now never
    // had one, and the queue kept the message but not the code behind it.
    if (item.status === 'failed') return result.tone === 'bad' ? result : { tone: 'bad', title: 'Scan refused', message: item.error ?? 'The server would not take this scan.', code: result.code };
    const response = item.response;
    if (!response) return result;
    if (response.outcome === 'rejected') return { tone: 'bad', title: titleFor(response.reasonCode), message: response.message, code: result.code };
    if (response.outcome !== 'accepted') return { tone: 'warn', title: response.message, message: words.notTaken, code: result.code };
    return words.accepted(response, result.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, resultFor, queue, state.version, words]);

  const submit = useCallback(
    async (code: string, override?: { reason: string }) => {
      if (!site || !j.current.contextId) return;
      const kicker = kickerNow();
      const request: ScanRequest = {
        // Made at the moment of capture and reused unchanged on every retry. An override is a new
        // scan, not a retry of the refused one, so it gets its own id.
        clientScanId: uuid(),
        siteId: site.id,
        mode: w.current.mode,
        contextId: j.current.contextId,
        code,
        scannedAt: new Date().toISOString(),
        ...(override ? { override } : {}),
      };
      const outcome = await queue.submit(request, j.current.context);
      // Only now: the queue settles the scan a turn before this line runs, and until the screen has
      // the answer in hand the panel describes the last scan. A refusal the screen takes over itself
      // — the count ended under the phone — never shows as this box's refusal, not for one commit.
      setResultFor(request.clientScanId);
      const pretty = formatCode(code);

      if (outcome.state === 'queued') {
        feedback('warn');
        // Asked of the queue, not read off the render: the write that decides it happens inside the
        // submit above, so the value this screen rendered with is a scan old.
        if (queue.storageOk()) setResult({ tone: 'warn', title: 'Saved on this phone', message: `${w.current.locks} stays locked until it has sent.`, code: pretty });
        else setResult({ tone: 'warn', title: 'Waiting to send', message: 'This phone could not save it — keep this screen open until it goes.', code: pretty });
        return;
      }

      if (outcome.state === 'failed') {
        if (j.current.onJobEnded?.(outcome.error)) return;
        feedback('bad');
        if (outcome.error.status === 401) {
          setResult({ tone: 'bad', title: 'Signed out', message: outcome.error.message, code: pretty });
          showRed({ title: 'Signed out', message: outcome.error.message, code: pretty, kicker, note: `${w.current.unchanged}. Sign in, then scan this box again.`, exit: 'login' });
          return;
        }
        const fatal = w.current.fatal[outcome.error.code] ?? { title: 'Scan refused', note: `${w.current.unchanged}. The server would not take this scan.` };
        setResult({ tone: 'bad', title: fatal.title, message: outcome.error.message, code: pretty });
        showRed({ title: fatal.title, message: outcome.error.message, code: pretty, kicker, note: fatal.note, exit: 'back' });
        return;
      }

      const response: ScanResponse = outcome.response;
      j.current.onResponse(response);
      if (response.outcome === 'rejected') {
        feedback('bad');
        const title = titleFor(response.reasonCode);
        setResult({ tone: 'bad', title, message: response.message, code: pretty });
        const overridable = !override && w.current.overridable?.(response) ? code : undefined;
        showRed({ title, message: response.message, code: pretty, kicker, note: w.current.refusalNote, exit: 'resume', overridable });
        return;
      }
      if (response.outcome === 'duplicate' || response.outcome === 'ignored') {
        feedback('warn');
        setResult({ tone: 'warn', title: response.message, message: w.current.notTaken, code: pretty });
        return;
      }
      feedback('ok');
      setResult(w.current.accepted(response, pretty));
    },
    [kickerNow, queue, showRed, site, titleFor],
  );

  const handleCode = useCallback(
    (raw: string) => {
      const code = normalizeCode(raw);
      if (!code) {
        // Nothing is sent: the server cannot make sense of it, and a round trip only delays saying so.
        feedback('bad');
        setResult(null);
        setResultFor(null);
        // A test label is refused by name, as Receive and Ship refuse it: it is never a box.
        if (testCodeKind(raw)) showRed({ title: 'Test sticker', message: reasonMessage('TEST_STICKER'), code: raw.slice(0, 24), kicker: kickerNow(), note: w.current.refusalNote, exit: 'resume' });
        else showRed({ title: 'Unknown sticker', message: "This doesn't look like one of our stickers.", code: raw.slice(0, 24), kicker: kickerNow(), note: w.current.refusalNote, exit: 'resume' });
        return;
      }
      void submit(code);
    },
    [kickerNow, showRed, submit],
  );

  return { ...state, result, shownResult, red, setRed, submit, handleCode };
}

/** The red screen for a scan, with its exits: the next box, back to the job's list, or sign-in. */
export function ScanRedScreen({
  red,
  resumeLabel,
  back,
  onDismiss,
  secondary,
}: {
  red: ScanRed | null;
  resumeLabel: string;
  /** The job's list: "Back to transfers", "Back to counts". */
  back: { to: string; label: string };
  onDismiss: () => void;
  secondary?: { label: string; onClick: () => void };
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  if (!red) return null;
  const label = red.exit === 'resume' ? resumeLabel : red.exit === 'back' ? back.label : 'Sign in again';
  return (
    <RedScreen
      title={red.title}
      message={red.message}
      code={red.code}
      kicker={red.kicker}
      note={red.note}
      continueLabel={label}
      secondary={secondary}
      onContinue={() => {
        onDismiss();
        if (red.exit === 'back') nav(back.to);
        // Asking again who this is lets the auth guard see the 401 and take the phone to sign-in.
        if (red.exit === 'login') void qc.invalidateQueries({ queryKey: qk.me });
      }}
    />
  );
}

export { s as kit };
